import { Typography } from 'components/DesignSystemV2/Typography';
import { IconsContentQuote } from 'components/Icon/SVGIcons';
import { LazyImage } from 'components/LazyImage';
import { VideoThumbnail } from 'components/VideoThumbnail';
import React from 'react';
import { desktopFirstV3 } from 'style/breakpointsv3';
import { colorsV2 } from 'style/colors-v2';
import styled from 'styled-components';
import { toScreen } from 'utils/media-query/responsive.util';

const CardInfoItem = styled.div`
  border-radius: 24px;
  border: 1px solid ${colorsV2.neutral20};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${colorsV2.white100};
  transform: translate(-25%, -25%);
`;

const CardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: ${({ $videoPos }) =>
    $videoPos === 'right' ? 'row' : 'row-reverse'};
  gap: 28px;
  flex: 1;

  ${toScreen(desktopFirstV3.sm)} {
    flex-direction: column;
    align-items: center;
  }
`;

const CardFooter = styled.div`
  padding: 24px;
  background-color: ${colorsV2.neutral5};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  border-top: 1px solid ${colorsV2.neutral20};
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  svg {
    width: 32px;
    height: 32px;
  }
`;
const Info = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 8px;
`;

const ContentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${toScreen(desktopFirstV3.xs)} {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const LeftContentInfo = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 40px) auto;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const AvatarWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${colorsV2.white100};
  box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;
`;

const Score = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${colorsV2.white100};
  border: 1px solid ${colorsV2.neutral20};
  border-radius: 16px;
  box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;
  flex-shrink: 0;
`;

const ReviewCard = ({
  review,
  onPlayVideo,
  footerTitle = '',
  videoPosition = 'right'
}) => {
  const {
    description,
    thumbnailUrl,
    videoUrl,
    avatar,
    fullName,
    schoolName,
    score,
    type,
    classStudied
  } = review;
  return (
    <CardInfoItem className="card-info">
      <CardContent $videoPos={videoPosition}>
        <Description>
          <IconWrapper>
            <IconsContentQuote />
          </IconWrapper>
          {description.split('\n').map((line, idx) => (
            <Typography
              key={idx}
              variant="regular/16-28"
              color={colorsV2.neutral180}
              v3
            >
              {line}
            </Typography>
          ))}
        </Description>
        <VideoThumbnail
          width={130}
          aspectRatio="130/233"
          poster={thumbnailUrl}
          onClick={() => onPlayVideo(videoUrl)}
        />
      </CardContent>
      <CardFooter>
        <Info>
          <Typography variant="semi-bold/14-20" color={colorsV2.neutral120} v3>
            {footerTitle}
          </Typography>

          <ContentInfo>
            <LeftContentInfo>
              <AvatarWrapper>
                <LazyImage className="" src={avatar} alt="student-avatar" />
              </AvatarWrapper>
              <div>
                <Typography
                  variant="semi-bold/16-24"
                  color={colorsV2.neutral180}
                  v3
                >
                  {fullName}
                </Typography>
                <Typography
                  variant="regular/14-20"
                  color={colorsV2.neutral120}
                  v3
                >
                  {schoolName}
                </Typography>
              </div>
            </LeftContentInfo>
            {score ? (
              <Score>
                <Typography
                  variant="bold/20-28"
                  color={colorsV2.primary100}
                  type="span"
                  v3
                >
                  {score}
                </Typography>
                <Typography
                  variant="semi-bold/14-20"
                  color={colorsV2.neutralPrimary}
                  type="span"
                  v3
                >
                  {type}
                </Typography>
              </Score>
            ) : classStudied ? (
              <Score>{classStudied}</Score>
            ) : null}
          </ContentInfo>
        </Info>
      </CardFooter>
    </CardInfoItem>
  );
};

export default ReviewCard;
